import { AppConfiguration, AppConfigService } from 'src/app/core/service/app-config.service';
import { Component, OnInit, Input, OnDestroy, ViewChild, ViewEncapsulation, ElementRef, AfterViewChecked } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { SettingService } from 'src/app/core/service/setting.service';
import { TranslateService } from '@ngx-translate/core';
import { DocumentGatewayService } from 'src/app/core/service/document-gateway.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { LocalStorageService } from 'ngx-webstorage';
import { PatientEventService } from 'src/app/core/service/patient-event.service';
import { UserService } from 'src/app/core/service/user.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ReferralComponent } from 'src/app/referral/referral.component';
import { MemberService } from 'src/app/core/service/member.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { FhirService } from 'src/app/core/service/fhir.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  standalone: false,
  selector: 'app-patient-summary',
  templateUrl: './patient-summary.component.html',
  styleUrls: ['./patient-summary.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PatientSummaryComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() public id: number;
  @Input() public showClinicalSummary = true;

  @ViewChild('summaryTabstrip') public summaryTabstrip: TabStripComponent;
  @ViewChild('historyTabstrip') public historyTabstrip: TabStripComponent;
  @ViewChild('referral') public referral: ReferralComponent;
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  public member: any;

  /**
   * Router event subscription
   */
  public routeParamSubscription: Subscription;
  public queryParamSubscription: Subscription;

  public patientLabel = {};
  public limitEvents: number;
  public limitEventsText = {};
  public patientEvents = [];
  public tabCounts: any;
  public referralsCollapsed = false;
  public patientHistoriesCollapsed = false;
  public patientEventsCollapsed = false;
  public patientPinnedResourcesCollapsed = false;
  public clinicalSummaryEnabled = false;
  public documentQueryEnabled = false;
  public documentQueryInProgress = false;
  public showHeader = false;
  public communityEnabled = false;
  public contactsEnabled = false;
  public appointmentsEnabled = false;
  public attachmentsEnabled = false;
  public notesEnabled = false;
  public messagesEnabled = false;
  public wellbeingEnabled = false;
  public pinnedResourcesEnabled = false;
  public patientCareTeamsEnabled = false;
  public eventsEnabled = false;
  public tabSelected = 0;
  public eventPage = 0;
  public referralsEnabled = false;
  public mrnStrategy: string;
  public hasExternalSearchSources = false;
  public fhrViewEnabled = false;
  public goalsEnabled = false;
  public auditMemberEnabled = false;
  public addReferralsEnabled = false;

  /**
   * Default size of the right-side pane.
   */
  private paneSize = '25%';

  private messagingSubscription: Subscription;
  private appConfig: AppConfiguration;

  constructor(
    private dictionaryService: DictionaryService,
    private patientEventService: PatientEventService,
    private memberService: MemberService,
    private route: ActivatedRoute,
    private messengerService: MessengerService,
    private settingService: SettingService,
    private translate: TranslateService,
    private appConfigService: AppConfigService,
    private documentGatewayService: DocumentGatewayService,
    private messageHandlerService: MessageHandlerService,
    private storageService: LocalStorageService,
    private userService: UserService,
    private router: Router,
    private fhirService: FhirService,
    private authenticationService: AuthenticationService,
  )
  {
    this.paneSize = this.storageService.retrieve('patient-summary-sidebar-size') || '25%';
    if (this.storageService.retrieve('collapsible-section-patient-events') === null)
    {
      this.storageService.store('collapsible-section-patient-events', true);
    }
    if (this.storageService.retrieve('collapsible-section-patient-histories') === null)
    {
      this.storageService.store('collapsible-section-patient-histories', true);
    }

    this.appConfig = this.appConfigService.getConfig();
    this.clinicalSummaryEnabled = this.appConfig.clinicalViewer &&
      this.appConfig.clinicalViewer.baseUrl !== '' && this.appConfig.clinicalViewer.baseUrl !== null;

    // subscribe to route parameter changes.
    this.routeParamSubscription = this.route.params.subscribe({
      next: (params: Params) =>
      {
        this.id = +params.id;
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        // subscribe to open new message dialog from task card view.
        if (message.type === 'openComposeMessageDialog' && message.data)
        {
          this.summaryTabstrip.selectTab(1);
          setTimeout(() =>
          {
            // Make component enabled
            let tabIndex = 4;
            if (!this.contactsEnabled) { tabIndex -= 1; }
            if (!this.attachmentsEnabled) { tabIndex -= 1; }
            this.historyTabstrip.selectTab(tabIndex);
            setTimeout(() =>
            {
              // Redirect message to component list
              this.messengerService.sendMessage(new MessageSubject('openComponentComposeMessageDialog', message.data));
            });
          });
        }
        else if (message && message.type && message.type === 'wsDocumentQueryStatusChanged')
        {
          const wsMessage = JSON.parse(message.data.body);
          switch (wsMessage.status)
          {
            case 'IN_PROGRESS':
              this.documentQueryInProgress = true;
              break;
            case 'COMPLETED':
            case 'COMPLETED_IN_ERROR':
              this.documentQueryInProgress = false;
              break;
          }
        }
        else if (message.type === 'openAppointmentEditDialog' && message.data)
        {
          // Make component enabled
          this.summaryTabstrip.selectTab(1);
          setTimeout(() =>
          {
            let tabIndex = 6;
            if (!this.contactsEnabled) { tabIndex -= 1; }
            if (!this.attachmentsEnabled) { tabIndex -= 1; }
            if (!this.messagesEnabled) { tabIndex -= 1; }
            if (!this.notesEnabled) { tabIndex -= 1; }
            this.historyTabstrip.selectTab(tabIndex);
            setTimeout(() =>
            {
              // Redirect message to component list
              this.messengerService.sendMessage(new MessageSubject('openComponentAddAppointmentDialog', message.data));
            });
          });
        }
        else if (message.type === 'openEditCareTeamMemberPatientSummary' && message.data)
        {
          this.summaryTabstrip.selectTab(0);
          this.onTabSelect({ index: 0 });
          setTimeout(() =>
          {
            this.messengerService.sendMessage(new MessageSubject('openCareTeamMemberPatient', message.data));
          });
        }
        else if (message.type === 'newReferralForCategory' && message.data && message.data.categoryId)
        {
          setTimeout(() =>
          {
            this.messengerService.sendMessage(new MessageSubject('searchForServicesToReferred', {
              categories: [message.data.categoryId],
              referralReassign: true,
              context: 'member',
              parentCaseId: message.data.caseId,
              excludedServices: [message.data.excludeReferredServiceId],
            }));
          }, 500);
        }
        else
        {
          // subscribe to case manager update messages.
          switch (message.type)
          {
            case 'caseManagerUpdated':
            case 'casePriorityUpdated':
            case 'caseHistoryUpdated':
            case 'getCaseDetailsData':
            case 'timelineVisibilityChanged':
              this.refreshAll(2500);
              break;
            case 'refreshSummaryCounts':
              this.getMemberCountTabs();
              this.messengerService.sendMessage(new MessageSubject('refreshInsurance'));
              break;
          }
        }
      }
    });
  }

  public get rightPaneSize(): string
  {
    return this.paneSize;
  }

  public set rightPaneSize(newSize: string)
  {
    this.paneSize = newSize;
    this.storageService.store('patient-summary-sidebar-size', newSize);
    this.autoExpandCollapseHistory();
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.documentQueryEnabled = sessionSetting.oneRecordEnabled;
        this.communityEnabled = sessionSetting.communityEnabled;
        this.contactsEnabled = (sessionSetting.contactsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.contactsRoles) : false;
        this.appointmentsEnabled = (sessionSetting.appointmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.appointmentsRoles) : false;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;
        this.notesEnabled = (sessionSetting.notesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.notesRoles) : false;
        this.messagesEnabled = (sessionSetting.messagesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;
        this.wellbeingEnabled = (sessionSetting.wellBeingRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.wellBeingRoles) : false;
        this.pinnedResourcesEnabled = (sessionSetting.pinnedResourcesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.pinnedResourcesRoles) : false;
        this.patientCareTeamsEnabled = (sessionSetting.patientCareTeamsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.patientCareTeamsRoles) : false;
        this.eventsEnabled = (sessionSetting.eventsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.eventsRoles) : false;
        this.referralsEnabled = sessionSetting.communityEnabled && ((sessionSetting.referralsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.referralsRoles) : false);
        this.fhrViewEnabled = (sessionSetting.ehrViewUsers !== '') ?
          this.userService.isUserInList(this.authenticationService.currentUser.id, sessionSetting.ehrViewUsers) : false;
        this.mrnStrategy = sessionSetting.mrnStrategy;
        this.goalsEnabled = (sessionSetting.goalsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.goalsRoles) : false;
        this.auditMemberEnabled = (sessionSetting.auditMemberRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.auditMemberRoles) : false;
        this.addReferralsEnabled = (sessionSetting.addClientReferralsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addClientReferralsRoles) : false;

        this.dictionaryService.getCachedPatientSearchSources().subscribe({
          next: (data) =>
          {
            this.hasExternalSearchSources = data.filter(x => x.code !== 'CHANNELS').length > 0;
          }
        });

        if (sessionSetting.oneRecordEnabled)
        {
          this.documentGatewayService.getLastQueryDocument(this.id).subscribe({
            next: (data) =>
            {
              if (data !== null && data.status !== null)
              {
                this.documentQueryInProgress = (data.status === 'IN_PROGRESS');
              }
            }
          });
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.getMember();

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.limitEvents = sessionSetting.eventPastDaysLimit !== undefined ? sessionSetting.eventPastDaysLimit : 30;
        this.translate.get(['LAST-N-DAYS']).subscribe({
          next: (res) =>
          {
            this.limitEventsText = res['LAST-N-DAYS'].replace('{{value}}', this.limitEvents);
          }
        });
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.queryParamSubscription = this.route.queryParams.subscribe({
      next: (params) =>
      {
        const tabSelection = params.tab || null;
        if (tabSelection !== null)
        {
          if (tabSelection === 'clinical-records')
          {
            setTimeout(() =>
            {
              this.summaryTabstrip.selectTab(1);
              setTimeout(() =>
              {
                let tabIndex = 8;
                if (!this.contactsEnabled) { tabIndex -= 1; }
                if (!this.attachmentsEnabled) { tabIndex -= 1; }
                if (!this.messagesEnabled) { tabIndex -= 1; }
                if (!this.notesEnabled) { tabIndex -= 1; }
                if (!this.appointmentsEnabled) { tabIndex -= 1; }
                this.historyTabstrip.selectTab(tabIndex);
              }, 500);
            }, 500);
          }
        }
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.updateSplitterBar();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.queryParamSubscription !== undefined) { this.queryParamSubscription.unsubscribe(); }
  }

  public getMemberCountTabs(): void
  {
    this.memberService.getMemberCountTabs(this.id).subscribe({
      next: (data) =>
      {
        this.tabCounts = data;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public openEHRView(): void
  {
    this.fhirService.saveChannelsPatientId(this.member.id);
    this.router.navigateByUrl(`/fhir/summary`);
  }

  /**
   * Open the task dialog and build the task form.
   */
  public initiateTask(assigneeId: number): void
  {
    this.summaryTabstrip.selectTab(1);
    setTimeout(() =>
    {
      this.historyTabstrip.selectTab(1);
      // Redirect task to component list
      setTimeout(() =>
      {
        this.messengerService.sendMessage(new MessageSubject('openComponentAddTaskDialog', assigneeId));
      });
    });
  }

  /**
   * Opens the external Clinical Viewer link in a new browser tab.
   */
  public openClinicalViewer(): void
  {
    const url = `${this.appConfig.clinicalViewer.baseUrl}?FromPortal=1&MRN=${this.member.mrn}`;
    window.open(url, '_blank');
  }

  public queryForDocuments(memberId: number): void
  {
    this.documentGatewayService.sendQueryDocumentRequest(memberId).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CLINICAL-RECORD-REQUESTED');
        this.getMemberCountTabs();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public onTabSelect(e: any): void
  {
    this.tabSelected = e.index;
    this.getMemberHistory();
  }

  public newReferral(): void
  {
    this.referral.newReferral();
  }

  public getReferralHistory(): void
  {
    this.memberService.requestReferralsHistory(this.member.id).subscribe({
      complete: () =>
      {
        this.messageHandlerService.displaySuccess('REQUEST-REFERRAL-HISTORY-SENT');
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public referralsOpenStateChanged(e: boolean): void
  {
    this.referralsCollapsed = e;
  }

  public searchEvents(offset: number): void
  {
    // If offset is 0, then this is the first page and need to reset.
    if (offset === 0)
    {
      this.eventPage = 0;
      this.patientEvents = [];
      this.patientEventService.reset();
    }

    this.patientEventService.searchEvents(offset, 20, { memberId: this.id })
      .subscribe({
        next: (events) =>
        {
          events.data.forEach(e => { this.patientEvents.push(e); });
          this.autoExpandCollapseHistory();
        }
      });
  }

  public patientHistoryScrolled(): void
  {
    this.eventPage = this.eventPage + 1;
    this.searchEvents(this.eventPage);
  }

  public showTooltip(e: ElementRef): void
  {
    this.tooltipDir.toggle(e, true);
  }
  
  public hideTooltip(): void
  {
    this.tooltipDir.hide();
  }

  public updateSplitterBar()
  {
    // do not run if no splitter is loaded
    if (!this.splitter) { return; }

    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetWidth : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  private getMember(): void
  {
    // get Member by ID from REST service
    this.memberService.getMember(this.id).subscribe({
      next: (data) =>
      {
        this.member = data;
        this.showHeader = true;
        this.getMemberCountTabs();
        this.saveRecentView();
      }
    });
  }

  private getMemberHistory()
  {
    if (this.tabSelected === 1)
    {
      // Fetch patient events
      this.searchEvents(0);
    }
  }

  private refreshAll(delayTime: number): void
  {
    setTimeout(() =>
    {
      this.getMemberCountTabs();
      this.getMemberHistory();
      this.messengerService.sendMessage(new MessageSubject('reloadCaseListDetailComponent'));
    }, delayTime);
  }

  private autoExpandCollapseHistory(): void
  {
    if (this.patientEvents && this.patientEvents && this.paneSize)
    {
      const size = +this.paneSize.replace('%', '');
      this.patientEvents.forEach(e => { e.open = size >= 30; });
    }
  }

  private saveRecentView(): void
  {
    this.memberService.saveRecentView(this.id).subscribe({
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
